import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Tab, Nav, Card, Badge, Dropdown, Button, Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import api from "./../../../api";
import usePermission from "../../../hooks/usePermission";

// Import tabs
import MaterialTab from "./tabs/MaterialTab";
import POTab from "./tabs/POTab";
import MIRTab from "./tabs/MIRTab";
import BillingTab from "./tabs/BillingTab";
import JMRTab from "./tabs/JMRTab";
import TCTab from "./tabs/TCTab";
import TeamTab from "./tabs/TeamTab";
import DPRTab from "./tabs/DPRTab";
import Overview from "./tabs/Overview";
import HandoverTab from "./tabs/HandoverTab";
import DocumentsTab from "./tabs/DocumentsTab";
import GanttChartTab from "./tabs/GanttChartTab";
import MeasurementSheetTab from "./tabs/MeasurementSheetTab";
import ContractualWoTab from "./tabs/ContractualWoTab";
import DeliveryTab from "./tabs/DeliveryTab";

import TDSList from "../DocumentControl/TDSList";
import TCList from "../DocumentControl/TCList";

const WorkOrderDetails = () => {
 const { id } = useParams();
 const [searchParams] = useSearchParams();
 const projectFromUrl = searchParams.get("project") || "";
 const [workOrder, setWorkOrder] = useState(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const [refreshing, setRefreshing] = useState(false);

 const { hasPermission } = usePermission();
 const user = useSelector((state) => state.auth?.userDetails || {});

 const normalize = (str) => (str || "").toLowerCase().replace(/[:\s_-]/g, "");
 const userPerms = (user?.permissionNames || []).map((p) => normalize(p));
 const hasBillingPerm =
 userPerms.includes("readbillings") ||
 userPerms.includes("managebillings") ||
 (user?.role?.name || user?.roleNamze || "").toLowerCase() === "admin";

 const allTabs = [
 { name: "Overview", icon: "info", component: Overview, permission: "read:workorders" },
 { name: "DPR", icon: "bar-chart-2", component: DPRTab, permission: "read:dprs" },
 { name: "Team", icon: "users", component: TeamTab, permission: "read:teams" },
 { name: "Gantt Chart", icon: "calendar", component: GanttChartTab, permission: "read:gantt" },
 { name: "Drawing", icon: "pencil", component: DocumentsTab, permission: "read:documents", props: { filterType: "Drawing" } },
 { name: "TDS/Manual", icon: "file-text", component: TDSList, permission: "read:documents", props: { isTab: true } },
 { name: "TC/Warranty Card", icon: "check-circle", component: TCList, permission: "read:documents", props: { isTab: true } },
 { name: "Indent", icon: "home", component: MaterialTab, permission: "read:indents" },
 { name: "PO", icon: "file-text", component: POTab, permission: "read:pos" },
 { name: "Contractual WO", icon: "file-text", component: ContractualWoTab, permission: "read:contractualwo" },
 { name: "Delivery", icon: "truck", component: DeliveryTab, permission: "read:deliveries" },
 { name: "MIR", icon: "clipboard", component: MIRTab, permission: "read:mirs" },
 { name: "JMR", icon: "check-square", component: JMRTab, permission: "read:jmrs" },
 { name: "Measurement Sheet", icon: "file-text", component: MeasurementSheetTab, permission: "read:measurement-sheets" },
 { name: "T & C Report", icon: "shield", component: TCTab, permission: "read:tcs" },
 { name: "Handover", icon: "briefcase", component: HandoverTab, permission: "read:handovers" },
 { name: "Documents", icon: "folder", component: DocumentsTab, permission: "read:documents" },
 { name: "Billing", icon: "credit-card", component: BillingTab, permission: "read:billings" },
 ];

 const woTabs = useMemo(() => {
 return allTabs.filter(tab => !tab.permission || hasPermission(tab.permission));
 }, [hasPermission]);

 const fetchWorkOrder = useCallback(async () => {
 try {
 setError(null);
 const response = await api.get(`/work-orders/${id}`);
 if (response.data.success) {
 setWorkOrder(response.data.data);
 } else {
 setError("Failed to fetch work order");
 }
 } catch (error) {
 console.error("Error fetching work order:", error);
 setError(error.response?.data?.message || "Error loading work order");
 } finally {
 setLoading(false);
 setRefreshing(false);
 }
 }, [id]);

 useEffect(() => {
 if (id) {
 setLoading(true);
 fetchWorkOrder();
 }
 }, [id, fetchWorkOrder]);

 const handleRefresh = () => {
 setRefreshing(true);
 fetchWorkOrder();
 };

 const formatDate = (dateString) => {
 if (!dateString) return "—";
 const date = new Date(dateString);
 return date.toLocaleDateString("en-US", {
 year: "numeric",
 month: "long",
 day: "numeric",
 });
 };

 const handleExportSummary = () => {
 console.log("handleExportSummary triggered. workOrder:", workOrder);
 if (!workOrder) {
 alert("Work order data not loaded yet.");
 return;
 }

 // Role-based check
 const userRole = (user.roleName || "").toLowerCase();
 const assignedWorkOrders = user.assignedWorkOrders || [];
 const hasAccess = userRole === "admin" || assignedWorkOrders.includes(workOrder._id?.toString());

 if (!hasAccess) {
 alert("You do not have permission to export this data.");
 return;
 }

 try {
 const wb = XLSX.utils.book_new();

 /* =========================
 SHEET 1: SUMMARY
 ========================= */
 const summaryRows = [
 ["--- WORK ORDER DETAILS ---", ""],
 ["Field", "Value"],
 ["WO Number", workOrder.woNo || workOrder.woNumber || "-"],
 ["SAC Code", workOrder.sacCode || "995461"],
 ["Scope", workOrder.scope || "-"],
 ["Status", workOrder.status || "-"],
 ];

 if (hasBillingPerm) {
 summaryRows.push(["Value", workOrder.value || 0]);
 summaryRows.push(["Total Amount", workOrder.totalAmount || 0]);
 }

 summaryRows.push(
 ["", ""],
 ["--- PROJECT & CLIENT ---", ""],
 ["Project Name", workOrder.project?.projectName || "-"],
 ["Client Name", workOrder.client?.name || "-"],
 ["", ""],
 ["--- TIMELINE ---", ""],
 ["Start Date", formatDate(workOrder.startDate)],
 ["End Date", formatDate(workOrder.endDate)]
 );

 const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
 summarySheet["!cols"] = [{ wch: 35 }, { wch: 60 }];
 XLSX.utils.book_append_sheet(wb, summarySheet, "WO Summary");

 /* =========================
 SHEET 2: BOQ ITEMS
 ========================= */
 const rawBoq = workOrder.boqItems || [];
 console.log("Raw BOQ items to export:", rawBoq);

 const boqRows = [
 ["Sr No", "Line No (wlino)", "Item Name", "Description", "Unit", "Quantity"]
 ];

 if (hasBillingPerm) {
 boqRows[0].push("Rate", "Total Amount");
 }

 boqRows[0].push("HSN/SAC", "Type", "Is Non-Billable");

 rawBoq.forEach((b, i) => {
 const qty = Number(b.quantity) || 0;
 const prc = Number(b.price) || 0;
 const amt = b.amount || (qty * prc);

 const row = [
 i + 1,
 b.wlino || "-",
 b.itemName || "-",
 b.description || "-",
 b.unit || "-",
 qty
 ];

 if (hasBillingPerm) {
 row.push(prc, amt);
 }

 row.push(
 b.hsnSac || workOrder.sacCode || "995461",
 b.measurementType || "quantity",
 b.isNonBillable ? "Yes" : "No"
 );

 boqRows.push(row);
 });

 if (rawBoq.length === 0) {
 boqRows.push(["-", "-", "No BOQ Items Found", "-", "-", 0, 0, 0, "-", "-", "-"]);
 }

 const boqSheet = XLSX.utils.aoa_to_sheet(boqRows);
 
 const boqCols = [
 { wch: 8 }, // Sr No
 { wch: 15 }, // wlino
 { wch: 40 }, // Item Name
 { wch: 60 }, // Description
 { wch: 10 }, // Unit
 { wch: 12 }, // Quantity
 ];

 if (hasBillingPerm) {
 boqCols.push(
 { wch: 15 }, // Rate
 { wch: 20 } // Total Amount
 );
 }

 boqCols.push(
 { wch: 15 }, // HSN/SAC
 { wch: 15 }, // Type
 { wch: 15 } // Is Non-Billable
 );

 boqSheet["!cols"] = boqCols;

 XLSX.utils.book_append_sheet(wb, boqSheet, "BOQ Items");

 const exportFileName = `WorkOrder_Full_Export_${workOrder.woNo || workOrder._id}.xlsx`;
 console.log("Writing XLSX file:", exportFileName);
 XLSX.writeFile(wb, exportFileName);
 console.log("XLSX file written successfully.");
 } catch (err) {
 console.error("Excel Export Error:", err);
 alert("Failed to generate Excel file. Check console for details.");
 }
 };

 const getStatusColor = (status) => {
 switch (status) {
 case "In Progress": return "warning";
 case "Completed": return "success";
 case "Approved": return "info";
 case "Pending": return "secondary";
 case "Closed": return "dark";
 default: return "primary";
 }
 };

 if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /><p className="mt-3">Loading...</p></div>;
 if (error) return <div className="alert alert-danger"><h4>Error!</h4><p>{error}</p><hr /><Link to={projectFromUrl ? `/work-orders?project=${projectFromUrl}` : "/work-orders"} className="btn btn-primary">Back</Link></div>;
 if (!workOrder) return <div className="alert alert-warning"><h4>Not Found</h4><p>Work order doesn't exist.</p><Link to={projectFromUrl ? `/work-orders?project=${projectFromUrl}` : "/work-orders"} className="btn btn-primary">Back</Link></div>;

 return (
 <div className="row">
 <div className="col-xl-12">
 <Tab.Container defaultActiveKey={woTabs.length > 0 ? woTabs[0].name.toLowerCase().replace(/\s/g, "-") : "overview"}>
 <div className="custom-tab-1">
 <Card className="mt-4 shadow-sm">
 <Card.Body className="d-flex justify-content-between align-items-center">
 <Nav as="ul" className="nav-tabs border-0 mb-0">
 {woTabs.map((tab, i) => (
 <Nav.Item as="li" key={i}>
 <Nav.Link eventKey={tab.name.toLowerCase().replace(/\s/g, "-")}>
 <i className={`la la-${tab.icon} me-2`} />
 {tab.name}
 </Nav.Link>
 </Nav.Item>
 ))}
 </Nav>
 
 <Button 
 variant="light" 
 className="btn-icon-only rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0"
 style={{ width: 38, height: 38, backgroundColor: '#fff', border: '1px solid #eee' }}
 onClick={handleRefresh}
 disabled={refreshing}
 title="Refresh Data"
 >
 {refreshing ? (
 <Spinner animation="border" size="sm" />
 ) : (
 <i className="fas fa-sync-alt text-primary"></i>
 )}
 </Button>
 </Card.Body>
 </Card>

 <Tab.Content className="pt-4">
 {woTabs.map((tab, i) => {
 const Component = tab.component;
 const eventKey = tab.name.toLowerCase().replace(/\s/g, "-");
 return (
 <Tab.Pane eventKey={eventKey} key={i}>
 {tab.name === "Overview" ? (
 <Overview
 id={id}
 workOrder={workOrder}
 formatDate={formatDate}
 getStatusColor={getStatusColor}
 handleExportSummary={handleExportSummary}
 />
 ) : (
 <Component workOrderId={id} workOrder={workOrder} {...(tab.props || {})} />
 )}
 </Tab.Pane>
 );
 })}
 </Tab.Content>
 </div>
 </Tab.Container>
 </div>
 </div>
 );
};

export default WorkOrderDetails;
